import { backgroundTexture } from './background-texture';
import { runWithFPS } from './run-with-fps';
import { vertexShaderSource, fragmentShaderSource } from './shaders';

const SCALE = 2;

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    throw new Error(`Error compiling shader: ${gl.getShaderInfoLog(shader)}`);
  }
  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
) {
  const program = gl.createProgram()!;
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    throw new Error(`Error linking program: ${gl.getProgramInfoLog(program)}`);
  }
  return program;
}

export type Options = {
  refraction: number;
  offset: number;
  speed: number;
  curvature: number;
  width: number;
  fps: number;
};

function initAIBackground(options: Options) {
  let time = -15;
  let ready = false;
  let needResize = false;
  let currentRoot: HTMLElement | null = null;

  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';

  const tryGl = canvas.getContext('webgl');
  if (!tryGl) {
    throw new Error('WebGL not supported');
  }
  const gl = tryGl;

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

  const program = createProgram(gl, vertexShader, fragmentShader);
  gl.useProgram(program);
  const uTimeLocation = gl.getUniformLocation(program, 'uTime');
  const uAspectRatioLocation = gl.getUniformLocation(program, 'uAspectRatio');
  const uOffsetLocation = gl.getUniformLocation(program, 'uOffset');
  const uCurvatureLocation = gl.getUniformLocation(program, 'uCurvature');
  const uDistortionLocation = gl.getUniformLocation(program, 'uDistortion');
  const uSizeLocation = gl.getUniformLocation(program, 'uSize');
  const uLineWidthLocation = gl.getUniformLocation(program, 'uLineWidth');

  function setCanvasSizeMultiplier(n: number) {
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width * n;
    canvas.height = height * n;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform1f(uAspectRatioLocation, width / height);

    gl.uniform2f(uSizeLocation, width * n, height * n);
  }

  const resizeObserver = new ResizeObserver(() => {
    needResize = true;
    if (canvas.width === 0) {
      setCanvasSizeMultiplier(SCALE);
    }
  });

  // плоскость с текстурными координатами
  const positions = new Float32Array([
    -1.0,
    -1.0,
    0.0,
    0.0,
    1.0,
    -1.0,
    1.0,
    0.0,
    -1.0,
    1.0,
    0.0,
    1.0,
    1.0,
    1.0,
    1.0,
    1.0
  ]);

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

  const aPosition = gl.getAttribLocation(program, 'aPosition');
  gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 4 * 4, 0);
  gl.enableVertexAttribArray(aPosition);

  const aTexCoord = gl.getAttribLocation(program, 'aTexCoord');
  gl.vertexAttribPointer(aTexCoord, 2, gl.FLOAT, false, 4 * 4, 2 * 4);
  gl.enableVertexAttribArray(aTexCoord);

  const indices = new Uint16Array([0, 1, 2, 2, 1, 3]);

  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);

  function setTexture(url: string) {
    const image = new Image();
    image.src = url;
    image.crossOrigin = 'anonymous';
    image.onload = function () {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

      ready = true;
    };
  }

  const registeredOptions: Partial<Record<keyof Options, (value: any) => void>> = {};
  function initOption<Key extends keyof Options>(
    id: Key,
    {
      location,
      cb,
      transformer = (a) => a
    }: {
      location?: WebGLUniformLocation | null;
      transformer?: (value: any) => Options[Key];
      cb?: (value: Options[Key]) => void;
    }
  ) {
    const updateValue = (value: any) => {
      const normalizedValue = transformer(value);
      if (location && typeof normalizedValue === 'number') {
        gl.uniform1f(location, normalizedValue);
      }
      cb?.(normalizedValue);
    };
    registeredOptions[id] = updateValue;
    updateValue(options[id]);
  }

  initOption('refraction', {
    location: uDistortionLocation,
    transformer: Number
  });
  initOption('offset', {
    location: uOffsetLocation,
    transformer: Number
  });
  initOption('width', {
    location: uLineWidthLocation,
    transformer(n) {
      return Number(n) * 10 * SCALE;
    }
  });
  initOption('curvature', {
    location: uCurvatureLocation,
    transformer: Number
  });
  let speedRate = 0;
  initOption('speed', {
    cb: (value) => {
      speedRate = value;
    },
    transformer: Number
  });
  let fps = 30;
  initOption('fps', {
    transformer: Number,
    cb: (value) => {
      if (!Number.isNaN(value)) {
        fps = value;
      }
    }
  });
  setTexture(backgroundTexture);

  function render() {
    if (!ready || !currentRoot) {
      return;
    }

    if (needResize) {
      setCanvasSizeMultiplier(SCALE);
      needResize = false;
    }
    time += 0.005 * speedRate * (120 / fps);
    gl.uniform1f(uTimeLocation, time);

    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  }

  runWithFPS(render, fps);
  return {
    canvas,
    render,
    updateOption<Key extends keyof Options>(id: Key, value: Options[Key]) {
      registeredOptions[id]?.(value);
    },
    updateOptions(options: Partial<Options>) {
      const keys = Object.keys(options) as Array<keyof Options>;
      for (const id of keys) {
        registeredOptions[id]?.(options[id]);
      }
    },
    attach(root: HTMLElement) {
      currentRoot = root;
      currentRoot.appendChild(canvas);
      resizeObserver.observe(root);
    },
    release() {
      if (currentRoot) {
        currentRoot.removeChild(canvas);
        resizeObserver.unobserve(currentRoot);
        currentRoot = null;
      }
    }
  };
}

export type BackgroundAnimation = ReturnType<typeof backgroundAnimation.init>;

export const backgroundAnimation = {
  init: initAIBackground
};
